import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';

export const ContactSection = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-14 sm:py-20"
      style={{ background: 'var(--bg)' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
            Contact
          </p>
          <h2
            className="font-display font-extrabold tracking-[-0.03em] mb-4 max-w-[18ch]"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', color: 'var(--white)' }}
          >
            Let us talk.
          </h2>
        </ScrollReveal>

        <div className="grid lg:grid-cols-5 gap-10 mt-10">
          {/* Form */}
          <div className="lg:col-span-3">
            <ScrollReveal delay={0.08}>
              <form data-testid="contact-form" className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Name</label>
                    <Input
                      name="name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="font-body text-sm h-11 rounded-[3px]"
                      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--b1)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Email</label>
                    <Input
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="font-body text-sm h-11 rounded-[3px]"
                      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--b1)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Phone</label>
                    <Input
                      name="phone"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+44 7XXX XXX XXX"
                      className="font-body text-sm h-11 rounded-[3px]"
                      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--b1)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Service Interest</label>
                    <select
                      name="service"
                      data-testid="contact-service-select"
                      value={form.service}
                      onChange={handleChange}
                      className="w-full h-11 rounded-[3px] px-3 font-body text-sm appearance-none"
                      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: form.service ? 'var(--white)' : 'var(--te)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--b1)'; e.target.style.boxShadow = 'none'; }}
                    >
                      <option value="" style={{ background: 'var(--l1)' }}>Select a service</option>
                      <option value="defend" style={{ background: 'var(--l1)' }}>Acodia Defend</option>
                      <option value="learn" style={{ background: 'var(--l1)' }}>Acodia Learn</option>
                      <option value="launch" style={{ background: 'var(--l1)' }}>Acodia Launch</option>
                      <option value="reach" style={{ background: 'var(--l1)' }}>Acodia Reach</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Message</label>
                  <textarea
                    name="message"
                    data-testid="contact-message-textarea"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your goals..."
                    rows={4}
                    className="w-full rounded-[3px] px-3 py-3 font-body text-sm resize-vertical"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)', minHeight: '110px' }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--b1)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  className="btn-shimmer font-body font-medium text-sm px-8 py-3 rounded-[3px] transition-all duration-200"
                  style={{ background: 'var(--a)', color: '#fff', height: 'auto' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--a-bright)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 14px 40px rgba(139,92,246,0.35)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--a)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Send size={14} className="mr-2" />
                  Send Message
                </Button>
              </form>
            </ScrollReveal>
          </div>

          {/* Details Card */}
          <div className="lg:col-span-2">
            <ScrollReveal delay={0.16}>
              <div
                className="p-6 rounded-lg h-full"
                style={{
                  background: 'rgba(10,10,18,0.88)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid var(--b1)',
                }}
              >
                <h3 className="font-display text-lg font-bold mb-6" style={{ color: 'var(--white)' }}>Get in Touch</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <Mail size={16} style={{ color: 'var(--a)', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>Email</p>
                      <p className="font-body text-sm" style={{ color: 'var(--white)' }}>hello@acodia.co.uk</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone size={16} style={{ color: 'var(--a)', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>Phone</p>
                      <p className="font-body text-sm" style={{ color: 'var(--white)' }}>+44 (0) 20 7946 0958</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} style={{ color: 'var(--a)', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>Address</p>
                      <p className="font-body text-sm" style={{ color: 'var(--white)' }}>Acodia HQ<br />London N1, United Kingdom</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={16} style={{ color: 'var(--a)', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>Hours</p>
                      <p className="font-body text-sm" style={{ color: 'var(--white)' }}>Mon–Fri: 09:00–18:00 GMT</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};
